"use client";

/**
 * Private Lesson Public Details Page
 * 
 * REQUIREMENTS:
 * - Display private lesson program details in a format consistent with class programs
 * - Show pricing (one rate per active lesson length, plus lesson credit packages)
 * - Show booking policies (advance booking, cancellation, payment timing from the lesson's own settings)
 * - Allow navigation to booking calendar
 * - Label who the booking is with (gym vs coach), but route every availability
 *   model through the same booking entry point
 * - Explain that lesson credit packages use credits; each lesson length's
 *   duration option says how many credits one booking uses
 * 
 * Design follows the same pattern as [programType]/[programId]/ProgramDetailClient.tsx
 * for consistency (that route's `page.tsx` is now the server shell that renders
 * metadata and the crawler summary).
 */

import React, { useState, useEffect, type ReactNode } from 'react';
import { useParams, useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import { brandTextColor, readableTextOn, SURFACE_GRAY_50 } from '@/utils/branding/brandColorContrast';
import { Button } from '@/components/ui/button';
import Alert from '@/components/ui/alert/Alert';
import { Badge } from "@/components/ui/Badge";
import { createLogger } from '@/utils/logger/devLogger';
import { formatCurrencyWholeOrCents as formatCurrency } from '@/utils/formatters/currencyUtils';
import PageLoading from '@/components/common/PageLoading';
import SummaryBelowPlaceholder from '@/components/seo/SummaryBelowPlaceholder';
import { useKeepScrollAcrossReload } from '@/hooks/ui/useKeepScrollAcrossReload';
import { StaffRegistrationMessage } from '@/components/common/StaffRegistrationGuard';
import PreviewAsParentButton from '@/components/programs/registration/preview/PreviewAsParentButton';
import { clearPreviewLessonCart } from './book/types';
import { useUserRole } from '@/context/UserRoleContext';
import { PublicAccountMenu } from '@/components/common/PublicAccountMenu';
import { useSelfServiceAccess } from '@/hooks/auth/useSelfServiceAccess';
import { useOrganization } from '@/context/OrganizationContext';
import {
  formatPrivateLessonPackageCoverage,
  formatPrivateLessonPackageLabel,
  getPrivateLessonCreditRuleText,
} from '@/utils/private-lessons/packageCreditCopy';
import { activeDurationChoicesFromProgram } from '@/utils/private-lessons/durationOptions';
// Family / Guardian Access (U7c): gate the booking entry point as early as the
// class catalog does. A secondary guardian who lacks "Can register this athlete"
// for the active family sees the "View Available Times" CTA disabled with a
// plain-English reason here, instead of walking through slot selection only to
// hit the per-athlete gate on the confirm step. Inert while the flag is off.
import { useFamilyAthleteGate } from '@/hooks/family/useFamilyAthleteGate';
import { isInvitedAthleteLogin } from '@/utils/auth/parentDashboardAccess';
import { CANNOT_REGISTER_ATHLETE_MESSAGE } from '@/utils/family/registrationPermissionCopy';
import { useIsMobileWebView } from '@/hooks/useIsMobileWebView';
import { summarizeRefundPolicy } from '@/utils/private-lessons/refundPolicySummary';
import { describePrivateLessonPaymentTiming } from '@/utils/private-lessons/lessonPaymentTimingCopy';
import PublicPriceExtrasNote from '@/components/programs/PublicPriceExtrasNote';

const logger = createLogger('PrivateLessonPublicPage');

export interface PrivateLessonDetailClientProps {
  /**
   * The crawler-visible program summary the SERVER already rendered, or null
   * when this program is not publishable.
   *
   * Same contract as the class detail route's: it sits below the loading screen
   * and stands in for the error screen, so the served HTML carries the lesson's
   * real lengths and prices, and the client swaps it for the interactive page once its own fetch
   * resolves. See `ProgramDetailClient` for why the client keeps its own mount
   * fetches rather than being seeded from this.
   */
  serverSummary?: ReactNode;
}

export default function PrivateLessonPublicPage({
  serverSummary = null,
}: PrivateLessonDetailClientProps = {}) {
  const params = useParams();
  const router = useRouter();
  // Inside the app's webview the parent already has the app's own navigation,
  // so the header's account controls are hidden here the same way they are on
  // the catalog this page is opened from.
  const isMobileWebView = useIsMobileWebView();
  const pathname = usePathname();
  const { currentUser, isLoading: loadingRole } = useUserRole();
  const selfServiceAccess = useSelfServiceAccess();
  const { currentOrganization } = useOrganization();
  const gymSlug = params?.gymSlug as string;
  const programId = params?.programId as string;
  
  const [privateLesson, setPrivateLesson] = useState<any | null>(null);
  const [organization, setOrganization] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bumped by the Try again button: the fetch below reads it, so incrementing
  // re-runs the load without a full page reload.
  const [retryCount, setRetryCount] = useState(0);

  // Family / Guardian Access early gate. blockAllInActiveFamily is true only when
  // the flag is on, an active family is resolved for THIS gym's org, and the
  // viewer cannot register the active family's athletes. Inert otherwise.
  const familyGate = useFamilyAthleteGate(organization?.id ?? null);

  useEffect(() => {
    const fetchData = async () => {
      if (!gymSlug || !programId) {
        setIsLoading(false);
        return;
      }

      try {
        logger.info(`Fetching public private lesson ${programId} for gym ${gymSlug}`);
        
        // Fetch organization by slug
        const orgResponse = await fetch(`/api/public/organizations/by-slug/${gymSlug}`);
        const orgResult = await orgResponse.json();

        if (!orgResponse.ok) {
          throw new Error(orgResult.error || 'Gym not found');
        }

        setOrganization(orgResult.organization);

        // Fetch private lesson program
        const plResponse = await fetch(`/api/public/programs/private_lessons/${programId}?gymSlug=${gymSlug}`);
        const plResult = await plResponse.json();

        if (!plResponse.ok) {
          throw new Error(plResult.error || 'Private lesson program not found');
        }

        setPrivateLesson(plResult.program);
        logger.info('Private lesson fetched successfully:', plResult.program);

      } catch (err: any) {
        logger.error('Error fetching private lesson:', err);
        setError(err.message || 'Failed to load private lesson');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [gymSlug, programId, retryCount]);

  // A reload part-way down would otherwise land in the crawler summary under the spinner.
  useKeepScrollAcrossReload(!isLoading && !loadingRole);

  if (isLoading || loadingRole) {
    // The server already wrote this program into the first response. It stays
    // in the page for crawlers, below the spinner, so a person never sees it
    // flash up before the real page replaces it.
    const spinner = <PageLoading variant="full-page" message="Loading program details..." />;
    if (serverSummary) return <SummaryBelowPlaceholder placeholder={spinner} summary={serverSummary} />;
    return spinner;
  }

  // Browsing is open to everyone; only the sign-up CTA is withheld. Covers
  // confirmed staff AND a viewer we could not identify — StaffRegistrationMessage
  // picks the right explanation.
  const selfServiceUnavailable = selfServiceAccess === 'staff' || selfServiceAccess === 'unconfirmed';

  if (error || !privateLesson || !organization) {
    // THE SERVER ALREADY WROTE THIS PROGRAM, SO AN ERROR SCREEN WOULD BE
    // DELETING CONTENT THE PAGE ALREADY HAS — and GOOGLEBOT is the reader that
    // makes it load-bearing. Googlebot renders: it fetches the page, gets the
    // summary, then runs the mount fetch above, which asks `/api/public/...`,
    // a path robots.txt disallows. Google's renderer refuses it, this branch
    // catches the failure, and without this guard the DOM Google indexes is
    // "Error" with no lesson length, price or policy in it. A person whose
    // connection dropped is better served the same way.
    //
    // Only the "Error" heading goes, so the page keeps exactly one `<h1>` — the
    // summary's.
    if (serverSummary) {
      return (
        <div className="min-h-screen bg-white">
          {serverSummary}
          {/*
            `role="alert"`, because this message is APPENDED to a page that
            already looks finished — a non-sighted visitor would otherwise be
            handed a complete-sounding lesson page and never told the booking
            half failed.
          */}
          <div role="alert" className="mx-auto max-w-5xl px-4 pb-10 sm:px-6 lg:px-8">
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <GoogleMaterialIcon name="warning" size="xs" className="mt-0.5 text-amber-600" />
              <div>
                {/*
                  A FIXED SENTENCE, never the caught error's own text. `error`
                  here is whatever the browser or the API said — "Failed to
                  fetch" is a real value it takes — and that is the developer
                  jargon the plain-English rule bans.
                */}
                <p className="text-sm text-amber-900">
                  We couldn&apos;t load the booking options for this program.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setError(null);
                      setIsLoading(true);
                      setRetryCount((count) => count + 1);
                    }}
                  >
                    Try again
                  </Button>
                  <Button variant="outline" onClick={() => router.push(`/programs/${gymSlug}`)}>
                    ← Back to Programs
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <GoogleMaterialIcon name="error" size="lg" className="text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600">{error || 'Program not found'}</p>
          <Button variant="outline" className="mt-4" onClick={() => router.push(`/programs/${gymSlug}`)}>
            ← Back to Programs
          </Button>
        </div>
      </div>
    );
  }

  // availability_managed_by uses 'gym' or 'coaches' (not the old 'gym_managed'/'coach_managed'/'hybrid')
  // Default to gym-managed if no model is set (most common case)
  const model = privateLesson.private_lessons?.availability_managed_by;
  const isGymManaged = !model || model === 'gym' || model === 'gym_managed' || model === 'hybrid';
  
  // Get branding colors
  const primaryColor = organization?.branding?.primary_color || organization?.primary_color || '#2563eb';
  
  // Get private lesson details
  const plDetails = privateLesson.private_lessons || {};
  const refundPolicy = summarizeRefundPolicy(plDetails);

  // The program's bookable lesson lengths, minutes ascending. Reads the
  // duration option rows embedded by the public program API (R2: prices on
  // this page must never come from the legacy 30/60 columns, which read
  // "no durations / $0" for a program offering other lengths).
  // U3 (R2, R8 / KTD5, KTD7, KTD8). A private lesson carries no location of its
  // own, so the route asks whether the gym can route a payment ANYWHERE — one answer, no partial
  // case, and therefore NO LOCATION TO NAME: `registration_locked_locations` is built only from
  // an offering's own locations, and a private lesson has none, so the headline here is always
  // the one that names nothing. A name-joining branch would be unreachable code pretending to a
  // case this surface cannot reach.
  //
  // `role="status"` (KTD8), matching the class detail page: the pre-fetch branch shows the
  // spinner with the server summary below it (`SummaryBelowPlaceholder`), and this notice
  // arrives with the rest of the page once the fetch lands.
  const registrationLocked = (privateLesson as any)?.registration_locked === true;
  const registrationLockTitle = "Registrations aren't available right now";

  const durationChoices = activeDurationChoicesFromProgram(plDetails);
  const durationMinutesList = durationChoices.map((choice) => choice.duration_minutes);
  const hasPricedLesson = durationChoices.some((choice) => Number(choice.price) > 0);
  const hasPricedOption = hasPricedLesson ||
    (plDetails.package_pricing ?? []).some((pkg: any) => Number(pkg?.price) > 0);
  const paymentTimingText = describePrivateLessonPaymentTiming({
    paymentModel: plDetails.payment_model,
    paymentTiming: plDetails.payment_timing,
    hasPricedLesson,
    sellsCreditPacks: (plDetails.package_pricing ?? []).length > 0,
  });

  const durationSummary = durationMinutesList.length === 0
    ? 'Flexible session lengths'
    : durationMinutesList.length === 1
      ? `${durationMinutesList[0]} minute sessions`
      : `${durationMinutesList.slice(0, -1).join(', ')} or ${durationMinutesList[durationMinutesList.length - 1]} minute sessions`;

  return (
    <div 
      className="min-h-screen bg-gray-50"
      style={{
        '--color-brand-gradient-start': primaryColor,
        '--color-brand-gradient-end': primaryColor,
        '--brand-primary': primaryColor,
      } as React.CSSProperties}
    >
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {organization?.logo_url ? (
                <img
                  src={organization.logo_url}
                  alt={organization.name}
                  className="h-12 w-auto"
                />
              ) : (
                <div>
                {/*
                  A `<p>`, NOT an `<h1>`. This page is about one PROGRAM, and the
                  program's name is its `<h1>` below — the same heading the
                  server summary owns before this component mounts, so the
                  document keeps one `<h1>` and keeps the SAME one across
                  hydration. Googlebot renders, so the heading it indexes is the
                  hydrated one; a gym name here would have every one of a gym's
                  program pages claiming the gym as its subject and competing
                  with the gym's own catalog page. It also fixes an older gap:
                  this branch only renders when the gym has no logo, so a gym
                  WITH a logo previously produced a page with no `<h1>` at all.
                */}
                  <p className="text-2xl font-bold text-gray-900">{organization?.name}</p>
                  <p className="text-sm text-gray-600">Program Details</p>
                </div>
              )}
            </div>
            <div className={`flex items-center gap-2${isMobileWebView ? ' hidden' : ''}`}>
              {currentUser ? (
                <>
                {/* Names the account this page is signed in as (#4897) — the
                    app can sign a browser sheet in from a link, so whose
                    account it is has to be visible. See SignedInAsBadge. */}
                <PublicAccountMenu user={currentUser} />
                </>
              ) : (
                <>
                  {/* Sign In is the primary (branded) CTA because most cheer parents
                      arriving here are returning customers with existing accounts.
                      Giving Create Account visual priority caused duplicate-account
                      409s when parents tapped the brand-colored button by habit.
                      Both buttons preserve pathname + current query string so deep
                      links with coach/date/utm params survive the signin round-trip. */}
                  <button
                    onClick={() => {
                      const target = (pathname || `/programs/${gymSlug}`) + (typeof window !== 'undefined' ? window.location.search : '');
                      router.push(`/signin?redirectTo=${encodeURIComponent(target)}`);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors hover:brightness-95"
                    style={{ backgroundColor: primaryColor, color: readableTextOn(primaryColor) }}
                  >
                    <GoogleMaterialIcon name="login" size="sm" />
                    Sign In
                  </button>
                  <button
                    onClick={() => {
                      const target = (pathname || `/programs/${gymSlug}`) + (typeof window !== 'undefined' ? window.location.search : '');
                      const params = new URLSearchParams({ redirectTo: target });
                      if (organization?.id) {
                        params.set('organizationId', organization.id);
                        if (organization?.name) params.set('organizationName', organization.name);
                      }
                      router.push(`/register?${params.toString()}`);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <GoogleMaterialIcon name="person_add" size="sm" />
                    Create Account
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back to Programs Link */}
        <Link 
          href={`/programs/${gymSlug}`}
          className="inline-flex items-center gap-2 mb-6 transition-colors hover:opacity-80"
          style={{ color: brandTextColor(primaryColor, 0, SURFACE_GRAY_50) }}
        >
          <GoogleMaterialIcon name="arrow_back" size="sm" />
          Back to Programs
        </Link>

        {/* Program Header */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-6">
          {/* Cover Image */}
          {privateLesson.cover_image_url && (
            <div className="relative h-64 bg-gray-100">
              <img
                src={privateLesson.cover_image_url}
                alt={privateLesson.name}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{privateLesson.name}</h1>
                <div className="flex items-center gap-3 flex-wrap">
                  <span 
                    className="inline-block px-3 py-1 text-sm font-medium rounded"
                    style={{ backgroundColor: `${primaryColor}1a`, color: brandTextColor(primaryColor, 10) }}
                  >
                    Private Lesson
                  </span>
                  {privateLesson.status && (
                    <Badge variant={privateLesson.status === 'active' ? 'success' : 'default'}>
                      {privateLesson.status.charAt(0).toUpperCase() + privateLesson.status.slice(1)}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Info */}
            <div className="flex flex-wrap items-center gap-4 text-gray-600">
              {/* Session durations available */}
              <div className="flex items-center gap-2">
                <GoogleMaterialIcon name="schedule" size="sm" />
                <span className="text-sm">
                  {durationSummary}
                </span>
              </div>
              {/* Booking model */}
              <div className="flex items-center gap-2">
                <GoogleMaterialIcon name="person" size="sm" />
                <span className="text-sm">
                  {isGymManaged ? 'Book with gym' : 'Book with coach'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
          {/* Main Content */}
          <div className="space-y-6">
            {/* Description */}
            {privateLesson.description && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <GoogleMaterialIcon name="description" size="sm" className="text-gray-500" />
                                  {/*
                  `h2`, NOT `h3`. U5 made the program's name the page's single
                  `h1` — it used to be the gym's name, and only in the no-logo
                  branch, so a gym WITH a logo rendered no `h1` at all. The whole
                  outline shifts up one with it. This page has no headings nested
                  inside its cards; the class detail page does, and they moved
                  too. `singleH1.test.tsx` renders this component with its
                  fetches resolving and counts the headings in the DOM;
                  `headingOrder.test.tsx` sweeps the source as a complement.
                */}
                  <h2 className="text-lg font-semibold text-gray-900">About This Program</h2>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{privateLesson.description}</p>
              </div>
            )}

            {/* Booking Policies */}
            {isGymManaged && (plDetails.max_days_advance || plDetails.min_hours_notice || refundPolicy.fullHours != null || refundPolicy.partial) ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <GoogleMaterialIcon name="policy" size="sm" className="text-gray-500" />
                  <h2 className="text-lg font-semibold text-gray-900">Booking Policies</h2>
                </div>
                <div className="space-y-4">
                  {(plDetails.max_days_advance || plDetails.min_hours_notice) ? (
                    <div className="flex items-start gap-3">
                      <div 
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${primaryColor}1a` }}
                      >
                        <GoogleMaterialIcon name="schedule" size="sm" style={{ color: brandTextColor(primaryColor, 10) }} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Booking Window</p>
                        <p className="text-sm text-gray-600">
                          {plDetails.max_days_advance ? `Book up to ${plDetails.max_days_advance} days in advance` : null}
                          {plDetails.max_days_advance && plDetails.min_hours_notice ? '. ' : null}
                          {plDetails.min_hours_notice ? `At least ${plDetails.min_hours_notice} hours notice required` : null}
                        </p>
                      </div>
                    </div>
                  ) : null}
                  
                  {paymentTimingText ? (
                    <div className="flex items-start gap-3">
                      <div 
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${primaryColor}1a` }}
                      >
                        <GoogleMaterialIcon name="payment" size="sm" style={{ color: brandTextColor(primaryColor, 10) }} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Payment</p>
                        <p className="text-sm text-gray-600" data-testid="lesson-payment-timing">
                          {paymentTimingText}
                        </p>
                      </div>
                    </div>
                  ) : null}
                  
                  <div className="flex items-start gap-3">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${primaryColor}1a` }}
                    >
                      <GoogleMaterialIcon name="event_busy" size="sm" style={{ color: brandTextColor(primaryColor, 10) }} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Cancellation Policy</p>
                      <p className="text-sm text-gray-600">
                        {refundPolicy.noAutomaticRefunds && 'If you cancel, refunds are up to the gym.'}
                        {refundPolicy.fullHours === 0 && 'Full refund if cancelled any time before the lesson. '}
                        {refundPolicy.fullHours != null && refundPolicy.fullHours > 0 &&
                          `Full refund if cancelled ${refundPolicy.fullHours}+ hours before. `}
                        {refundPolicy.partial &&
                          `${refundPolicy.partial.percent}% refund if cancelled ${refundPolicy.partial.hours}+ hours before.`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pricing Card */}
            <div className="bg-white rounded-2xl shadow-theme-lg border border-gray-200 p-6 sticky top-4">
              <div className="flex items-center gap-2 mb-4">
                <GoogleMaterialIcon name="payments" size="sm" className="text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">Pricing</h2>
              </div>

              <div className="space-y-4 mb-6">
                {/* Single Session Rates — one row per active lesson length */}
                {durationChoices.map((choice) => (
                  <div
                    key={choice.duration_minutes}
                    className="flex items-center justify-between pb-4 border-b border-gray-200"
                  >
                    <div>
                      <p className="text-sm text-gray-600">{choice.duration_minutes}-Minute Session</p>
                      <p className="text-xs text-gray-500">Single booking</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-gray-900">
                        {formatCurrency(choice.price)}
                      </p>
                    </div>
                  </div>
                ))}

                {/* Credit Pack Options */}
                {plDetails.package_pricing && plDetails.package_pricing.length > 0 && (
                  <div className="pt-2 space-y-3">
                    <div>
                      <p className="text-xs font-medium text-gray-900 uppercase">Credit Pack Options</p>
                      <p className="text-xs text-gray-500 mt-1">{getPrivateLessonCreditRuleText(durationChoices)}</p>
                    </div>
                    {plDetails.package_pricing.map((pkg: any, index: number) => (
                      <div key={index} className="flex items-start justify-between gap-3 text-sm">
                        <span className="text-gray-600">
                          <span className="block font-medium text-gray-700">
                            {formatPrivateLessonPackageLabel(Number(pkg.sessions), 'parent')}
                          </span>
                          <span className="block text-xs text-gray-500">
                            {formatPrivateLessonPackageCoverage(Number(pkg.sessions), {
                              durationChoices,
                            })}
                          </span>
                        </span>
                        <span className="text-gray-900 font-medium">{formatCurrency(pkg.price)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {hasPricedOption && (
                <PublicPriceExtrasNote programId={programId} />
              )}

              {/* Additional fees notice */}
              <div className="flex items-start gap-2 px-3 py-2.5 bg-gray-50 rounded-2xl border border-gray-100 mb-6">
                <GoogleMaterialIcon name="info" size="xs" className="text-gray-400 mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  {(privateLesson as any)?.private_lessons?.annual_membership_required === false
                    ? 'No annual gym membership fee is required for this private lesson.'
                    : 'An annual gym membership fee may be added at checkout, depending on your membership status.'}
                </p>
              </div>

              {/* Availability Status.
                  U3: "Available for Booking" and "Schedule your session today"
                  are both false above a notice saying registrations are not
                  available, so the block is suppressed rather than reworded —
                  the notice is the answer. */}
              {!registrationLocked && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-2xl">
                    <GoogleMaterialIcon name="check_circle" size="sm" className="text-green-600" />
                    <div>
                      <p className="text-sm font-semibold text-green-900">Available for Booking</p>
                      <p className="text-xs text-green-700">Schedule your session today</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Outside the call-to-action chain, so locked lesson pages show it too (#5634 KTD13). */}
              <PreviewAsParentButton
                programId={programId}
                surface="private_lesson"
                enabled={!!currentUser}
                enrollHref={`/programs/${gymSlug}/private-lessons/${programId}/book?preview=1`}
                onStart={() => clearPreviewLessonCart(programId)}
              />

              {/* CTA Button */}
              {registrationLocked ? (
                /* U3 (R2 / D1): this lesson's payment cannot be routed, so the
                   booking entry is replaced by the notice. Prices and coach
                   details stay visible above — "disable, don't hide". The
                   catalog card deliberately keeps "View Details" for private
                   lessons because it starts nothing; THIS is where the booking
                   step is stopped. */
                <div role="status">
                  <Alert
                    variant="warning"
                    title={registrationLockTitle}
                    data-testid="registration-locked-notice"
                  />
                </div>
              ) : selfServiceUnavailable ? (
                <StaffRegistrationMessage size="md" gymName={currentOrganization?.name} />
              ) : isInvitedAthleteLogin(currentUser) ? (
                <Alert variant="info" title="Please ask your parent or guardian to book this for you." />
              ) : familyGate.blockAllInActiveFamily ? (
                /* Family / Guardian Access early gate: the viewer cannot register
                   the active family's athletes, so we disable the booking entry
                   here (mirrors the class catalog's locked CTA) rather than letting
                   them pick a slot and hit the per-athlete gate on the confirm
                   step. The server gate stays as the backstop. */
                <div className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl text-sm text-gray-600 text-center">
                  <GoogleMaterialIcon name="lock" size="sm" className="mb-1 block mx-auto text-gray-400" />
                  {CANNOT_REGISTER_ATHLETE_MESSAGE}
                </div>
              ) : (
                <Button
                  variant="primary"
                  style={{ color: readableTextOn(primaryColor) }}
                  onClick={() => router.push(`/programs/${gymSlug}/private-lessons/${programId}/book`)}
                  className="w-full py-6 text-lg font-bold"
                  startIcon={<GoogleMaterialIcon name="calendar_today" size="sm" />}
                >
                  View Available Times
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
